/**
 * WHERE A LEAD ACTUALLY IS.
 *
 * Anthony: "they should be tagged New Lead, Text back campaign, Show the
 * last message in the tile box, basically show the stage that they are in
 * the text back campaign and the ones that are red or fine, but it's
 * confusing because ones that are probably in the back campaign say not
 * contacted. Keep it neat."
 *
 * ─── THE CONFUSION WAS REAL AND IT WAS OURS ────────────────────────────
 *
 * The card read "not contacted" off `enquiries.status`, which only moves to
 * `contacted` when the textback task successfully sends AND updates the
 * row. Every lead the old deployment texted, every lead texted by a person
 * from the phone, and every lead where the update failed after the send
 * still said "not contacted" — beside a red tag saying we had texted twice
 * and heard nothing. The card contradicted itself.
 *
 * So the stage is read from the CONVERSATION, which is the only thing that
 * knows what actually happened, and `status` is not consulted at all.
 *
 * ─── THE STAGES, IN THE ORDER THEY HAPPEN ──────────────────────────────
 *
 *   new         it came in and nothing has gone out yet
 *   opener      the first text is out, waiting
 *   nudged      the follow-up is out too, still waiting
 *   replied     they said something, which is the whole point
 *   talking     somebody at the shop has answered them
 *   called      somebody rang, which is what red asks for
 *   quoted      a price is out and the ball is with them
 *
 * A lead cannot be in two of these, and the one it is in is the newest
 * thing that happened, which is why this is a function over facts rather
 * than a column somebody has to remember to set.
 */

export type LeadStage =
  | "new" | "opener" | "nudged" | "replied" | "talking" | "called" | "quoted";

export const STAGE_LABEL: Record<LeadStage, string> = {
  new: "New lead",
  opener: "Textback sent",
  nudged: "Textback, nudged",
  replied: "They replied",
  talking: "Talking",
  called: "Rang them",
  quoted: "Quote out",
};

/**
 * The short line under the label. Deliberately says what happens NEXT
 * rather than restating the label, because a tile that repeats itself is a
 * tile people stop reading.
 */
export const STAGE_HINT: Record<LeadStage, string> = {
  new: "Nothing has gone out yet",
  opener: "Waiting on them",
  nudged: "Second text out, still nothing back",
  replied: "They are waiting on us",
  talking: "In conversation",
  called: "Somebody has spoken to them",
  quoted: "Waiting on a yes",
};

export type StageFacts = {
  /** Outbound texts to them since the lead came in. */
  textsOut: number;
  /** When they last said something, or null. */
  lastIn: number | null;
  /** When we last texted, or null. */
  lastOut: number | null;
  /** Somebody at the shop rang them. */
  called: boolean;
  /** A quote has been raised off this lead. */
  quoted: boolean;
};

export function stageOf(f: StageFacts): LeadStage {
  // A price out is the furthest along anything gets while still a lead.
  if (f.quoted) return "quoted";
  // Ringing somebody outranks any amount of texting, which is the whole
  // reason the red tag exists.
  if (f.called) return "called";

  if (f.lastIn) {
    // Who spoke last decides between "they are waiting on us" and "we are
    // in a conversation". Those need different things from a person.
    return f.lastOut && f.lastOut > f.lastIn ? "talking" : "replied";
  }
  if (f.textsOut >= 2) return "nudged";
  if (f.textsOut >= 1) return "opener";
  return "new";
}

/**
 * WHICH STAGES ARE WAITING ON THE SHOP. Used for the color, so the tile
 * and the front desk feed cannot disagree about whose move it is.
 */
export const onUs = (s: LeadStage): boolean => s === "replied";

/** Which stages mean nobody has heard a word back. */
export const stillQuiet = (s: LeadStage): boolean =>
  s === "new" || s === "opener" || s === "nudged";

/**
 * THE CALL THAT MADE THE LEAD IS NOT THEM ANSWERING.
 *
 * A lead from the phone line is born from an inbound call, and that call
 * sits in the ledger as the customer speaking. Read naively, every one of
 * those cards opened at "They replied" with "they wrote 2 min ago" before
 * anybody at the shop had done a thing. That call is how they arrived;
 * what counts as a reply is something they did AFTER the lead existed.
 *
 * A minute of slack either side of the stamp, because the ledger row and
 * the lead are written separately and their clocks need not agree to the
 * millisecond. A real second call inside that minute is the same ring
 * twice, not a conversation.
 */
export const OPENING_SLACK_MS = 60_000;

export function isOpeningCall(
  r: { direction: string; kind: string | null; occurred_at: string },
  leadCreatedAt: string | null | undefined,
): boolean {
  if (r.direction !== "inbound" || r.kind !== "call" || !leadCreatedAt) return false;
  const at = Date.parse(r.occurred_at);
  const born = Date.parse(leadCreatedAt);
  if (!Number.isFinite(at) || !Number.isFinite(born)) return false;
  return at <= born + OPENING_SLACK_MS;
}

/** How they last reached us, in the words the card uses. */
export const theyDid = (kind: string | null | undefined): string =>
  kind === "call" ? "they called" : "they wrote";
